// Mobile Widget Service
// Handles mobile-specific PWA features: home screen widgets, app shortcuts, and mobile optimizations

export interface WidgetConfig {
  id: string;
  name: string;
  description: string;
  icon: string;
  size: 'small' | 'medium' | 'large';
  url: string;
  data?: any;
  updateInterval?: number; // in minutes
}

export interface AppShortcut {
  name: string;
  shortName: string;
  description: string;
  icon: string;
  url: string;
  color?: string;
}

export interface MobileFeatures {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  supportsWidgets: boolean;
  supportsShortcuts: boolean;
  supportsPWA: boolean;
  displayMode: 'standalone' | 'minimal-ui' | 'fullscreen' | 'browser';
}

export class MobileWidgetService {
  private widgets: Map<string, WidgetConfig> = new Map();
  private shortcuts: AppShortcut[] = [];
  private isSupported: boolean;
  private mobileFeatures: MobileFeatures;

  constructor() {
    this.isSupported = this.checkSupport();
    this.mobileFeatures = this.detectMobileFeatures();
    this.initDefaultWidgets();
    this.initDefaultShortcuts();
  }

  /**
   * Check if mobile widgets are supported
   */
  private checkSupport(): boolean {
    return 'serviceWorker' in navigator && 
           'caches' in window &&
           (this.isMobileDevice() || this.isTabletDevice());
  }

  /**
   * Detect mobile device features
   */
  private detectMobileFeatures(): MobileFeatures {
    const userAgent = navigator.userAgent;
    const isMobile = this.isMobileDevice();
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    
    // Check display mode
    const displayMode = window.matchMedia('(display-mode: standalone)').matches ? 'standalone' :
                       window.matchMedia('(display-mode: minimal-ui)').matches ? 'minimal-ui' :
                       window.matchMedia('(display-mode: fullscreen)').matches ? 'fullscreen' : 'browser';

    return {
      isMobile,
      isIOS,
      isAndroid,
      supportsWidgets: isMobile && 'serviceWorker' in navigator,
      supportsShortcuts: isMobile && 'serviceWorker' in navigator,
      supportsPWA: isMobile && 'serviceWorker' in navigator,
      displayMode
    };
  }

  /**
   * Check if running on mobile device
   */
  isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Check if running on tablet device
   */
  isTabletDevice(): boolean {
    return /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)/i.test(navigator.userAgent);
  }

  /**
   * Get mobile features information
   */
  getMobileFeatures(): MobileFeatures {
    return this.mobileFeatures;
  }

  /**
   * Initialize default widgets
   */
  private initDefaultWidgets(): void {
    // Dashboard Widget
    this.addWidget({
      id: 'dashboard',
      name: 'Dashboard',
      description: 'Quick overview of business metrics',
      icon: '/assets/icons/dashboard-widget.png',
      size: 'medium',
      url: '/dashboard',
      updateInterval: 15
    });

    // Sales Widget
    this.addWidget({
      id: 'sales',
      name: 'Sales',
      description: 'Today\'s sales and revenue',
      icon: '/assets/icons/sales-widget.png',
      size: 'small',
      url: '/sales',
      updateInterval: 5
    });

    // Appointments Widget
    this.addWidget({
      id: 'appointments',
      name: 'Appointments',
      description: 'Upcoming appointments today',
      icon: '/assets/icons/appointment-widget.png',
      size: 'medium',
      url: '/appointments',
      updateInterval: 10
    });

    // Notifications Widget
    this.addWidget({
      id: 'notifications',
      name: 'Notifications',
      description: 'Recent system notifications',
      icon: '/assets/icons/notification-widget.png',
      size: 'small',
      url: '/notifications',
      updateInterval: 1
    });
  }

  /**
   * Initialize default app shortcuts
   */
  private initDefaultShortcuts(): void {
    this.shortcuts = [
      {
        name: 'New Sale',
        shortName: 'Sale',
        description: 'Start a new sale transaction',
        icon: '/assets/icons/sale-shortcut.png',
        url: '/pos/new-sale',
        color: '#28a745'
      },
      {
        name: 'New Appointment',
        shortName: 'Appt',
        description: 'Schedule a new appointment',
        icon: '/assets/icons/appointment-shortcut.png',
        url: '/appointments/new',
        color: '#17a2b8'
      },
      {
        name: 'Quick Reports',
        shortName: 'Reports',
        description: 'View business reports',
        icon: '/assets/icons/report-shortcut.png',
        url: '/reports',
        color: '#ffc107'
      },
      {
        name: 'Customer Search',
        shortName: 'Search',
        description: 'Find customer information',
        icon: '/assets/icons/search-shortcut.png',
        url: '/customers/search',
        color: '#6f42c1'
      }
    ];
  }

  /**
   * Add a new widget
   */
  addWidget(config: WidgetConfig): void {
    this.widgets.set(config.id, config);
    console.log(`Widget added: ${config.name}`);
    
    // Notify service worker about new widget
    this.notifyServiceWorker('WIDGET_ADDED', config);
  }

  /**
   * Remove a widget
   */
  removeWidget(widgetId: string): boolean {
    const deleted = this.widgets.delete(widgetId);
    if (deleted) {
      console.log(`Widget removed: ${widgetId}`);
      
      // Notify service worker about removed widget
      this.notifyServiceWorker('WIDGET_REMOVED', { id: widgetId });
    }
    return deleted;
  }

  /**
   * Get all widgets
   */
  getWidgets(): WidgetConfig[] {
    return Array.from(this.widgets.values());
  }

  /**
   * Get widget by ID
   */
  getWidget(widgetId: string): WidgetConfig | undefined {
    return this.widgets.get(widgetId);
  }

  /**
   * Update widget data
   */
  async updateWidgetData(widgetId: string, data: any): Promise<void> {
    const widget = this.widgets.get(widgetId);
    if (!widget) {
      throw new Error(`Widget not found: ${widgetId}`);
    }

    // Update widget data
    widget.data = data;
    
    // Store updated data in cache for offline access
    await this.cacheWidgetData(widgetId, data);
    
    // Notify service worker about data update
    this.notifyServiceWorker('WIDGET_DATA_UPDATED', { id: widgetId, data });
    
    console.log(`Widget data updated: ${widgetId}`);
  }

  /**
   * Cache widget data for offline access
   */
  private async cacheWidgetData(widgetId: string, data: any): Promise<void> {
    if ('caches' in window) {
      try {
        const cache = await caches.open('widget-data-v1');
        const response = new Response(JSON.stringify(data), {
          headers: { 'Content-Type': 'application/json' }
        });
        await cache.put(`/widget-data/${widgetId}`, response);
      } catch (error) {
        console.error('Failed to cache widget data:', error);
      }
    }
  }

  /**
   * Get cached widget data
   */
  async getCachedWidgetData(widgetId: string): Promise<any | null> {
    if ('caches' in window) {
      try {
        const cache = await caches.open('widget-data-v1');
        const response = await cache.match(`/widget-data/${widgetId}`);
        if (response) {
          return await response.json();
        }
      } catch (error) {
        console.error('Failed to get cached widget data:', error);
      }
    }
    return null;
  }

  /**
   * Get app shortcuts
   */
  getAppShortcuts(): AppShortcut[] {
    return this.shortcuts;
  }

  /**
   * Add app shortcut
   */
  addAppShortcut(shortcut: AppShortcut): void {
    this.shortcuts.push(shortcut);
    console.log(`App shortcut added: ${shortcut.name}`);
  }

  /**
   * Remove app shortcut
   */
  removeAppShortcut(name: string): boolean {
    const index = this.shortcuts.findIndex(s => s.name === name);
    if (index > -1) {
      this.shortcuts.splice(index, 1);
      console.log(`App shortcut removed: ${name}`);
      return true;
    }
    return false;
  }

  /**
   * Show mobile-specific install prompt
   */
  showMobileInstallPrompt(): void {
    if (!this.mobileFeatures.isMobile) {
      console.warn('Mobile install prompt only works on mobile devices');
      return;
    }

    if (this.mobileFeatures.isIOS) {
      this.showIOSInstallInstructions();
    } else if (this.mobileFeatures.isAndroid) {
      this.showAndroidInstallInstructions();
    }
  }

  /**
   * Show iOS-specific install instructions
   */
  private showIOSInstallInstructions(): void {
    const banner = document.createElement('div');
    banner.className = 'ios-install-banner';
    banner.style.cssText = `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
      color: white;
      padding: 20px;
      z-index: 9999;
      text-align: center;
      box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
      animation: slideUp 0.3s ease-out;
    `;

    banner.innerHTML = `
      <div style="margin-bottom: 15px;">
        <i class="cil-arrow-up" style="font-size: 24px; margin-bottom: 10px;"></i>
        <h4 style="margin: 0 0 10px 0;">Install eSpa247 POS</h4>
        <p style="margin: 0; opacity: 0.9;">
          Tap the <strong>Share</strong> button and then <strong>"Add to Home Screen"</strong>
        </p>
      </div>
      <button class="ios-install-btn" style="
        background: white;
        color: #007AFF;
        border: none;
        padding: 12px 24px;
        border-radius: 25px;
        font-weight: 600;
        cursor: pointer;
        margin-right: 10px;
      ">Got it!</button>
      <button class="ios-close-btn" style="
        background: none;
        border: 1px solid white;
        color: white;
        padding: 12px 24px;
        border-radius: 25px;
        cursor: pointer;
      ">Not now</button>
    `;

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideUp {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(banner);

    // Event listeners
    banner.querySelector('.ios-install-btn')?.addEventListener('click', () => {
      banner.remove();
    });

    banner.querySelector('.ios-close-btn')?.addEventListener('click', () => {
      banner.remove();
    });

    // Auto-remove after 15 seconds
    setTimeout(() => {
      if (banner.parentNode) {
        banner.remove();
      }
    }, 15000);
  }

  /**
   * Show Android-specific install instructions
   */
  private showAndroidInstallInstructions(): void {
    const banner = document.createElement('div');
    banner.className = 'android-install-banner';
    banner.style.cssText = `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, #4CAF50 0%, #2196F3 100%);
      color: white;
      padding: 20px;
      z-index: 9999;
      text-align: center;
      box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
      animation: slideUp 0.3s ease-out;
    `;

    banner.innerHTML = `
      <div style="margin-bottom: 15px;">
        <i class="cil-plus" style="font-size: 24px; margin-bottom: 10px;"></i>
        <h4 style="margin: 0 0 10px 0;">Install eSpa247 POS</h4>
        <p style="margin: 0; opacity: 0.9;">
          Tap the <strong>menu</strong> button and then <strong>"Add to Home Screen"</strong>
        </p>
      </div>
      <button class="android-install-btn" style="
        background: white;
        color: #4CAF50;
        border: none;
        padding: 12px 24px;
        border-radius: 25px;
        font-weight: 600;
        cursor: pointer;
        margin-right: 10px;
      ">Got it!</button>
      <button class="android-close-btn" style="
        background: none;
        border: 1px solid white;
        color: white;
        padding: 12px 24px;
        border-radius: 25px;
        cursor: pointer;
      ">Not now</button>
    `;

    document.body.appendChild(banner);

    // Event listeners
    banner.querySelector('.android-install-btn')?.addEventListener('click', () => {
      banner.remove();
    });

    banner.querySelector('.android-close-btn')?.addEventListener('click', () => {
      banner.remove();
    });

    // Auto-remove after 15 seconds
    setTimeout(() => {
      if (banner.parentNode) {
        banner.remove();
      }
    }, 15000);
  }

  /**
   * Create mobile-optimized manifest
   */
  createMobileManifest(): any {
    return {
      name: 'eSpa247 POS',
      short_name: 'eSpa247',
      description: 'Professional Salon Management System',
      start_url: '/',
      display: 'standalone',
      orientation: 'portrait-primary',
      theme_color: '#667eea',
      background_color: '#ffffff',
      icons: [
        {
          src: '/assets/icons/android-icon-192x192.png',
          sizes: '192x192',
          type: 'image/png',
          purpose: 'any maskable'
        },
        {
          src: '/assets/icons/android-icon-512x512.png',
          sizes: '512x512',
          type: 'image/png',
          purpose: 'any maskable'
        }
      ],
      shortcuts: this.shortcuts.map(shortcut => ({
        name: shortcut.shortName,
        short_name: shortcut.shortName,
        description: shortcut.description,
        url: shortcut.url,
        icons: [{ src: shortcut.icon, sizes: '96x96' }]
      })),
      categories: ['business', 'productivity', 'finance'],
      lang: 'en',
      dir: 'ltr'
    };
  }

  /**
   * Notify service worker about widget events
   */
  private notifyServiceWorker(type: string, data: any): void {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type,
        data,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Get mobile optimization recommendations
   */
  getMobileOptimizations(): string[] {
    const recommendations: string[] = [];
    
    if (!this.mobileFeatures.isMobile) {
      recommendations.push('This feature is designed for mobile devices');
      return recommendations;
    }

    if (this.mobileFeatures.displayMode === 'browser') {
      recommendations.push('Install the app for better mobile experience');
    }

    if (this.mobileFeatures.isIOS) {
      recommendations.push('Use Safari browser for best iOS compatibility');
      recommendations.push('Add to Home Screen for app-like experience');
    }

    if (this.mobileFeatures.isAndroid) {
      recommendations.push('Use Chrome browser for best Android compatibility');
      recommendations.push('Enable "Add to Home Screen" for quick access');
    }

    recommendations.push('Enable push notifications for real-time updates');
    recommendations.push('Use offline mode for better performance');

    return recommendations;
  }

  /**
   * Check if service is supported
   */
  getSupported(): boolean {
    return this.isSupported;
  }
}

// Create singleton instance
export const mobileWidgetService = new MobileWidgetService();
